/**
 * @callback PreflightHandlerFn
 * @param {Object} sideEffectData - The `sideEffectData` object from the request object
 */

/**
 * @typedef {Record<string, PreflightHandlerFn>} PreflightHandlers
 */

/**
 * @callback ResponseHandlerFn
 * @param {Object} response - The request's response data
 */

/**
 * @typedef {Record<string, ResponseHandlerFn>} ResponseHandlers
 */

/**
 * @typedef {Object} DataOptions
 * @property {PreflightHandlers} DataOptions.preflightHandlers
 * @property {ResponseHandlers} DataOptions.responseHandlers
 */

/**
 * @param {import('axios').AxiosInstance} axios
 */
export default function dataFactory(axios) {
  /**
   * Calls the matching `preflightHandlers[request.agilemdType](requestContext)` for each request
   * Calls the matching `responseHandlers[request.agilemdType](response)` for each response
   * @example
   * interceptData({
   *  preflightHandlers: { [REQUESTS.EVENT]: request => store.dispatch(networkRequestConfig(request)) },
   *  responseHandlers: { [REQUESTS.EVENT]: response => store.dispatch(networkResponse(response)) }
   * })
   * @function interceptData
   * @param {DataOptions} options
   * @returns {void}
   */
  return function interceptData({ preflightHandlers, responseHandlers }) {
    axios.interceptors.request.use(config => {
      const { sideEffectData: data = {}, agilemdType } = config;

      if (preflightHandlers[agilemdType]) {
        preflightHandlers[agilemdType](data);
      }

      return config;
    });

    axios.interceptors.response.use(res => {
      const {
        config: { agilemdType },
        data
      } = res;

      if (responseHandlers[agilemdType]) {
        responseHandlers[agilemdType](data);
      }

      return res;
    });
  };
}
